/**
 * Invoice Preview - Billing Module
 * Professional invoice preview with company branding, print and PDF download
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { makeStyles, Text, Button, Spinner, tokens } from '@fluentui/react-components';
import { ArrowLeftRegular, PrintRegular } from '@fluentui/react-icons';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

interface InvoiceItem {
  id: number;
  service_name: string;
  site_name: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

interface Company {
  legal_name: string;
  tagline: string;
  logo_url: string;
  registered_address: string;
  country: string;
  primary_phone: string;
  primary_email: string;
  website: string;
  primary_color: string;
}

interface Invoice {
  id: number;
  invoice_number: string;
  client_id: number;
  client_name: string;
  email: string;
  phone: string;
  address: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  currency: string;
  status: string;
  issue_date: string;
  due_date: string;
  notes: string;
  terms: string;
  items: InvoiceItem[];
  company: Company | null;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '24px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '100px',
    gap: '12px',
  },
  invoiceWrapper: {
    backgroundColor: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    borderRadius: '4px',
  },
  invoice: {
    padding: '48px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    color: '#1a1a1a',
    backgroundColor: 'white',
  },
  invoiceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '40px',
    paddingBottom: '24px',
    borderBottom: '2px solid #e1e1e1',
  },
  companySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  companyLogo: {
    maxHeight: '60px',
    maxWidth: '200px',
    objectFit: 'contain',
    marginBottom: '8px',
  },
  companyName: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: '0',
  },
  companyTagline: {
    fontSize: '13px',
    color: '#666',
    fontStyle: 'italic',
    margin: '0',
  },
  companyContact: {
    fontSize: '12px',
    color: '#555',
    lineHeight: '1.6',
    marginTop: '8px',
  },
  invoiceTitle: {
    textAlign: 'right',
  },
  invoiceTitleText: {
    fontSize: '32px',
    fontWeight: '700',
    color: tokens.colorBrandForeground1,
    margin: '0',
    letterSpacing: '2px',
  },
  invoiceNumber: {
    fontSize: '14px',
    color: '#555',
    marginTop: '8px',
  },
  invoiceMeta: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '40px',
    marginBottom: '40px',
  },
  metaSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  metaLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  metaValue: {
    fontSize: '14px',
    color: '#333',
    lineHeight: '1.5',
  },
  clientName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a1a1a',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '24px',
  },
  tableHeader: {
    backgroundColor: '#f8f8f8',
  },
  th: {
    padding: '14px 16px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: '600',
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #e1e1e1',
  },
  thRight: {
    padding: '14px 16px',
    textAlign: 'right',
    fontSize: '11px',
    fontWeight: '600',
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #e1e1e1',
  },
  tr: {
    borderBottom: '1px solid #eee',
  },
  td: {
    padding: '14px 16px',
    fontSize: '14px',
    color: '#333',
    verticalAlign: 'top',
  },
  tdRight: {
    padding: '14px 16px',
    fontSize: '14px',
    color: '#333',
    textAlign: 'right',
    verticalAlign: 'top',
  },
  serviceName: {
    fontWeight: '500',
  },
  siteName: {
    fontSize: '12px',
    color: '#666',
    marginTop: '2px',
  },
  totalsSection: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  totalsTable: {
    width: '280px',
  },
  totalsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    fontSize: '14px',
    color: '#555',
  },
  grandTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 0',
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a1a1a',
    borderTop: '2px solid #333',
    marginTop: '8px',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statusPending: {
    backgroundColor: '#fff3cd',
    color: '#856404',
  },
  statusPaid: {
    backgroundColor: '#d4edda',
    color: '#155724',
  },
  statusOverdue: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
  },
  footer: {
    marginTop: '48px',
    paddingTop: '24px',
    borderTop: '1px solid #e1e1e1',
  },
  footerNote: {
    fontSize: '12px',
    color: '#666',
    lineHeight: '1.6',
    marginBottom: '16px',
  },
  footerTerms: {
    fontSize: '11px',
    color: '#888',
    lineHeight: '1.6',
  },
  thankYou: {
    textAlign: 'center',
    marginTop: '32px',
    padding: '16px',
    backgroundColor: '#f9f9f9',
    borderRadius: '4px',
  },
  thankYouText: {
    fontSize: '14px',
    color: '#555',
    margin: '0',
  },
});

const InvoicePreviewFluentUI: React.FC = () => {
  const styles = useStyles();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invoiceRef = useRef<HTMLDivElement>(null);

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchInvoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get(`/get-invoice/${id}`);
      if (response.data.status === 'success') {
        setInvoice(response.data.data);
      } else {
        newToast('error', response.data.message || 'Invoice not found');
        navigate('/account/billing/generate-bill');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
      navigate('/account/billing/generate-bill');
    }
    setLoading(false);
  };

  const handlePrint = () => {
    // Add print-specific styles dynamically (only once)
    const printStyleId = 'invoice-print-styles';
    let printStyles = document.getElementById(printStyleId) as HTMLStyleElement | null;

    if (!printStyles) {
      printStyles = document.createElement('style');
      printStyles.id = printStyleId;
      printStyles.textContent = `
        @media print {
          body * {
            visibility: hidden !important;
          }
          #invoice-print-area, #invoice-print-area * {
            visibility: visible !important;
          }
          #invoice-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            padding: 20px !important;
            background: white !important;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
        }
      `;
      document.head.appendChild(printStyles);
    }

    // Trigger browser's native print dialog
    // User can print or select "Save as PDF" option
    window.print();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: invoice?.currency || 'GHS',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) {
      return '—';
    }
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'paid':
        return styles.statusPaid;
      case 'overdue':
        return styles.statusOverdue;
      default:
        return styles.statusPending;
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="medium" />
        <Text>Loading invoice...</Text>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className={styles.loadingContainer}>
        <Text>Invoice not found</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.actions}>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          onClick={() => navigate('/account/billing/generate-bill')}
        >
          Back
        </Button>
        <Button appearance="primary" icon={<PrintRegular />} onClick={handlePrint}>
          Print / Save as PDF
        </Button>
      </div>

      <div className={styles.invoiceWrapper} id="invoice-print-area">
        <div className={styles.invoice} ref={invoiceRef}>
          {/* Header with Company and Invoice Title */}
          <div className={styles.invoiceHeader}>
            <div className={styles.companySection}>
              {invoice.company?.logo_url && (
                <img
                  src={invoice.company.logo_url}
                  alt="Company Logo"
                  className={styles.companyLogo}
                />
              )}
              <h1 className={styles.companyName}>
                {invoice.company?.legal_name || 'Company Name'}
              </h1>
              {invoice.company?.tagline && (
                <p className={styles.companyTagline}>{invoice.company.tagline}</p>
              )}
              <div className={styles.companyContact}>
                {invoice.company?.registered_address && (
                  <div>{invoice.company.registered_address}</div>
                )}
                {invoice.company?.country && <div>{invoice.company.country}</div>}
                {invoice.company?.primary_phone && <div>Tel: {invoice.company.primary_phone}</div>}
                {invoice.company?.primary_email && (
                  <div>Email: {invoice.company.primary_email}</div>
                )}
                {invoice.company?.website && <div>{invoice.company.website}</div>}
              </div>
            </div>
            <div className={styles.invoiceTitle}>
              <h2 className={styles.invoiceTitleText}>INVOICE</h2>
              <div className={styles.invoiceNumber}>
                <strong>{invoice.invoice_number}</strong>
              </div>
              <div style={{ marginTop: '12px' }}>
                <span className={`${styles.statusBadge} ${getStatusClass(invoice.status)}`}>
                  {invoice.status.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {/* Meta Information */}
          <div className={styles.invoiceMeta}>
            <div className={styles.metaSection}>
              <span className={styles.metaLabel}>Bill To</span>
              <span className={styles.clientName}>{invoice.client_name}</span>
              <span className={styles.metaValue}>
                {invoice.address && <div>{invoice.address}</div>}
                {invoice.contact_name && <div>Attn: {invoice.contact_name}</div>}
                {invoice.phone && <div>Tel: {invoice.phone}</div>}
                {invoice.email && <div>{invoice.email}</div>}
              </span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div
                className={styles.metaSection}
                style={{ alignItems: 'flex-end', marginBottom: '16px' }}
              >
                <span className={styles.metaLabel}>Issue Date</span>
                <span className={styles.metaValue}>{formatDate(invoice.issue_date)}</span>
              </div>
              {invoice.due_date && (
                <div className={styles.metaSection} style={{ alignItems: 'flex-end' }}>
                  <span className={styles.metaLabel}>Due Date</span>
                  <span className={styles.metaValue}>{formatDate(invoice.due_date)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Items Table */}
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th} style={{ width: '40%' }}>
                  Description
                </th>
                <th className={styles.thRight}>Qty</th>
                <th className={styles.thRight}>Unit Price</th>
                <th className={styles.thRight}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={item.id || index} className={styles.tr}>
                  <td className={styles.td}>
                    <div className={styles.serviceName}>{item.service_name}</div>
                    {item.site_name && <div className={styles.siteName}>{item.site_name}</div>}
                    {item.description && <div className={styles.siteName}>{item.description}</div>}
                  </td>
                  <td className={styles.tdRight}>{item.quantity}</td>
                  <td className={styles.tdRight}>{formatCurrency(item.unit_price)}</td>
                  <td className={styles.tdRight}>{formatCurrency(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className={styles.totalsSection}>
            <div className={styles.totalsTable}>
              <div className={styles.totalsRow}>
                <span>Subtotal</span>
                <span>{formatCurrency(invoice.subtotal)}</span>
              </div>
              {invoice.tax_rate > 0 && (
                <div className={styles.totalsRow}>
                  <span>Tax ({invoice.tax_rate}%)</span>
                  <span>{formatCurrency(invoice.tax_amount)}</span>
                </div>
              )}
              <div className={styles.grandTotalRow}>
                <span>Total Due</span>
                <span>{formatCurrency(invoice.total_amount)}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            {invoice.notes && <div className={styles.footerNote}>{invoice.notes}</div>}
            {invoice.terms && (
              <div className={styles.footerTerms}>
                <strong>Terms & Conditions:</strong> {invoice.terms}
              </div>
            )}
            <div className={styles.thankYou}>
              <p className={styles.thankYouText}>Thank you for your business!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .${styles.invoiceWrapper}, .${styles.invoiceWrapper} * {
            visibility: visible;
          }
          .${styles.invoiceWrapper} {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            box-shadow: none;
          }
          .${styles.actions} {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default InvoicePreviewFluentUI;
